/**
 * Chart Color Assignment Utility
 *
 * Provides scalable, deterministic color assignment for chart entities
 * (models, agents, nodes, etc.)
 *
 * Strategy:
 * 1. Extract entity family (remove version/date suffixes for models)
 * 2. Hash family name to get base color from palette
 * 3. Generate shade variation for different versions within same family
 *
 * Examples:
 * - claude-3-5-sonnet-20241022 → family: claude-3-5-sonnet → color from palette + shade
 * - claude-3-5-sonnet-20240620 → family: claude-3-5-sonnet → same color + different shade
 * - agent_name_123 → hashed to consistent color from palette
 */

/**
 * Generate a large bright color palette (120 colors)
 * Uses HSL color space to generate evenly distributed, high-saturation, bright colors
 * Optimized for visibility in both light and dark modes
 */
function generateBrightPalette(count = 120) {
  const colors = [];
  const hueStep = 360 / count;

  for (let i = 0; i < count; i++) {
    const hue = Math.round(i * hueStep);
    // High saturation (85-95%) and medium-high lightness (60-75%) for bright, distinct colors
    const saturation = 85 + (i % 3) * 5; // Vary saturation: 85%, 90%, 95%
    const lightness = 60 + (i % 4) * 5;  // Vary lightness: 60%, 65%, 70%, 75%

    const rgb = hslToRgb(hue, saturation, lightness);
    colors.push(rgbToHex(rgb.r, rgb.g, rgb.b));
  }

  return colors;
}

/**
 * Large bright color palette (120 colors) for maximum entity differentiation
 * Generated programmatically with evenly spaced hues for optimal visual distinction
 */
const CHART_COLOR_PALETTE = generateBrightPalette(120);

/**
 * Extract model family by removing version suffixes
 *
 * Removes:
 * - Dates: -20241022, -241022
 * - Versions: -v1, -v2, -0125
 * - Hashes: -abc123def
 *
 * @param {string} modelName - Full model name
 * @returns {string} - Model family name
 */
function extractModelFamily(modelName) {
  if (!modelName) return '';

  return modelName
    .replace(/-\d{8}$/i, '')        // Remove -20241022 (8-digit date)
    .replace(/-\d{6}$/i, '')         // Remove -241022 (6-digit date)
    .replace(/-v?\d+$/i, '')         // Remove -v1, -2, -0125, etc.
    .replace(/-[a-f0-9]{6,}$/i, '')  // Remove hex hashes (6+ chars)
    .replace(/-\d{4}$/i, '');        // Remove -0125, etc.
}

/**
 * Simple string hash function
 *
 * @param {string} str - String to hash
 * @returns {number} - Hash value (non-negative integer)
 */
function hashString(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

/**
 * Get base color for a model family
 *
 * @param {string} familyName - Model family name
 * @returns {string} - Hex color code
 */
function getBaseColorForFamily(familyName) {
  const hash = hashString(familyName);
  const colorIndex = hash % CHART_COLOR_PALETTE.length;
  return CHART_COLOR_PALETTE[colorIndex];
}

/**
 * Convert hex color to RGB
 *
 * @param {string} hex - Hex color code (e.g., '#FF5733')
 * @returns {{r: number, g: number, b: number}} - RGB values (0-255)
 */
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : { r: 0, g: 0, b: 0 };
}

/**
 * Convert RGB to HSL
 *
 * @param {number} r - Red (0-255)
 * @param {number} g - Green (0-255)
 * @param {number} b - Blue (0-255)
 * @returns {{h: number, s: number, l: number}} - HSL values (h: 0-360, s/l: 0-100)
 */
function rgbToHsl(r, g, b) {
  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100)
  };
}

/**
 * Convert HSL to RGB
 *
 * @param {number} h - Hue (0-360)
 * @param {number} s - Saturation (0-100)
 * @param {number} l - Lightness (0-100)
 * @returns {{r: number, g: number, b: number}} - RGB values (0-255)
 */
function hslToRgb(h, s, l) {
  h /= 360;
  s /= 100;
  l /= 100;

  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };

    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;

    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255)
  };
}

/**
 * Convert RGB to hex
 *
 * @param {number} r - Red (0-255)
 * @param {number} g - Green (0-255)
 * @param {number} b - Blue (0-255)
 * @returns {string} - Hex color code
 */
function rgbToHex(r, g, b) {
  return '#' + [r, g, b].map(x => {
    const hex = x.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  }).join('');
}

/**
 * Adjust color lightness while keeping colors bright
 *
 * @param {string} hexColor - Hex color code
 * @param {number} offset - Lightness offset (-10 to +10 percentage points)
 * @returns {string} - Adjusted hex color code
 */
function adjustColorLightness(hexColor, offset) {
  const rgb = hexToRgb(hexColor);
  const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);

  // Adjust lightness, keeping colors in bright range (55-85%)
  // Narrower range to ensure all variations remain visible in both modes
  hsl.l = Math.max(55, Math.min(85, hsl.l + offset));

  const newRgb = hslToRgb(hsl.h, hsl.s, hsl.l);
  return rgbToHex(newRgb.r, newRgb.g, newRgb.b);
}

/**
 * Get shade variation for a specific model version within a family
 *
 * @param {string} fullModelName - Full model name with version
 * @param {string} familyName - Model family name
 * @returns {number} - Lightness offset (-10 to +10)
 */
function getVersionShade(fullModelName, familyName) {
  // Extract version suffix (everything after family name)
  const version = fullModelName.replace(familyName, '').replace(/^-/, '');

  if (!version) {
    return 0; // Base shade (no modification)
  }

  // Hash the version to get a shade offset
  const versionHash = hashString(version);
  const shadeOffset = (versionHash % 21) - 10; // Range: -10 to +10

  return shadeOffset;
}

/**
 * Get color for a chart entity (main entry point)
 *
 * Strategy:
 * 1. Extract entity family (e.g., claude-3-5-sonnet for models)
 * 2. Hash family to get base color
 * 3. If multiple versions exist, apply shade variation
 *
 * @param {string} entityName - Full entity name (model, agent, node, etc.)
 * @param {string[]} allEntities - All entity names in dataset (for version detection)
 * @returns {string} - Hex color code
 */
export function getChartColor(entityName, allEntities = []) {
  // Special case: "Other" bucket
  if (entityName === 'Other') {
    return '#8A88A8'; // Muted gray
  }

  // Extract family name
  const family = extractModelFamily(entityName);

  // Get base color for family
  const baseColor = getBaseColorForFamily(family);

  // Check if there are multiple versions of this family
  const familyVersions = allEntities.filter(m =>
    extractModelFamily(m) === family
  );

  // If only one version, use base color (no shade variation needed)
  if (familyVersions.length <= 1) {
    return baseColor;
  }

  // Generate shade variation for this version
  const shadeOffset = getVersionShade(entityName, family);
  return adjustColorLightness(baseColor, shadeOffset);
}

/**
 * Get colors for all chart entities (batch operation with caching)
 *
 * Returns a map of entityName → color for efficient lookups
 *
 * @param {string[]} entityNames - Array of entity names (models, agents, nodes, etc.)
 * @returns {Object} - Map of entityName to hex color
 */
export function getChartColors(entityNames) {
  const colors = {};
  entityNames.forEach(entityName => {
    colors[entityName] = getChartColor(entityName, entityNames);
  });
  return colors;
}
